import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { JobEnqueueResultDto, PagedResult, QueryOptions, SyncRunDto, SyncStatusDto } from '../lib/types';

function toQuery(options: QueryOptions): string {
  const p = new URLSearchParams();
  if (options.page) p.set('page', String(options.page));
  if (options.pageSize) p.set('pageSize', String(options.pageSize));
  if (options.sortBy) p.set('sortBy', options.sortBy);
  if (options.sortDesc) p.set('sortDesc', 'true');
  if (options.filter) p.set('filter', options.filter);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function useSyncStatus(refetchMs = 15_000) {
  return useQuery({
    queryKey: ['admin', 'sync', 'status'],
    queryFn: () => apiClient.get<SyncStatusDto>('/api/admin/sync/status'),
    refetchInterval: refetchMs
  });
}

export function useSyncRuns(options: QueryOptions) {
  return useQuery({
    queryKey: ['admin', 'sync', 'runs', options],
    queryFn: () => apiClient.get<PagedResult<SyncRunDto>>(`/api/admin/sync${toQuery(options)}`),
    refetchInterval: 15_000
  });
}

/** POST /api/admin/sync/{source}; invalidates the sync queries so status and runs refresh. */
export function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (source: string) => apiClient.post<JobEnqueueResultDto>(`/api/admin/sync/${source}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sync'] })
  });
}
